import type { WorkspaceAction } from '../utils/workspacePermissions'

/**
 * Which top-level views a viewer of a Preview/Public workspace is allowed to
 * open. Missing/false keys default to hidden. This is the Admin-controlled
 * surface allowlist (or the demo bundle's fixed mask for `LEARN`).
 */
export type WorkspaceSurfaceKey =
  | 'OVERVIEW'
  | 'DASHBOARD'
  | 'CALENDAR'
  | 'CHECKLISTS'
  | 'REPORTS'
  | 'ANALYSIS'
  | 'FORMS'
  | 'MEETINGS'
  | 'AUTOMATE'
  | 'DESIGN'
  | 'CHAT'
  | 'STOCK'
  | 'BUDGET'
  | 'QUOTA'
  | 'TEACHING'
  | 'TRAINING'
  | 'CONSTRUCTION'
  | 'HOSPITAL'
  | 'SITES'
  | 'DELIVERY'
  | 'SHOPS'
  | 'STAYS'
  | 'JOBS'
  | 'CLINICS'
  | 'PLANT'
  | 'RAID'
  | 'AI_MAGIC'
  | 'MANAGE'
  | 'SUMMARY'

export const WORKSPACE_SURFACE_KEYS: readonly WorkspaceSurfaceKey[] = [
  'OVERVIEW', 'DASHBOARD', 'CALENDAR', 'CHECKLISTS', 'REPORTS', 'ANALYSIS', 'FORMS',
  'MEETINGS', 'AUTOMATE', 'DESIGN', 'CHAT', 'STOCK', 'BUDGET', 'QUOTA',
  'TEACHING', 'TRAINING',
  'CONSTRUCTION', 'HOSPITAL', 'SITES', 'DELIVERY', 'SHOPS', 'STAYS', 'JOBS', 'CLINICS', 'PLANT',
  'RAID', 'AI_MAGIC', 'MANAGE', 'SUMMARY',
]

export const WORKSPACE_SURFACE_LABELS: Record<WorkspaceSurfaceKey, string> = {
  OVERVIEW: 'Overview',
  DASHBOARD: 'Dashboard',
  CALENDAR: 'Calendar',
  CHECKLISTS: 'Checklists',
  REPORTS: 'Reports',
  ANALYSIS: 'Analysis',
  FORMS: 'Forms',
  MEETINGS: 'Meetings',
  AUTOMATE: 'Automate',
  DESIGN: 'Design',
  CHAT: 'Chat',
  STOCK: 'Stock',
  BUDGET: 'Budget',
  QUOTA: 'Quota',
  TEACHING: 'Teaching',
  TRAINING: 'Training',
  CONSTRUCTION: 'Construction',
  HOSPITAL: 'Hospital',
  SITES: 'Sites',
  DELIVERY: 'Delivery',
  SHOPS: 'Shops',
  STAYS: 'Stays',
  JOBS: 'Jobs',
  CLINICS: 'Clinics',
  PLANT: 'Plant',
  RAID: 'RAID',
  AI_MAGIC: 'AI Magic',
  MANAGE: 'Detailed View',
  SUMMARY: 'Audit Summary',
}

export type WorkspaceSurfaceMask = {
  surfaces: Partial<Record<WorkspaceSurfaceKey, boolean>>
  /** Which `readonly.view_*` sensitive fields are unmasked. */
  sensitive: Partial<Record<Extract<WorkspaceAction, `readonly.view_${string}`>, boolean>>
  /** People roster visible in viewer UI. */
  showMemberRoster?: boolean
  /** Show real names vs role-labels only. */
  showRealNames?: boolean
  /** Offer "download as PDF" affordance on viewer surfaces. */
  allowExportPdf?: boolean
  /** Only messages within the last N days appear in Chat surfaces. */
  chatMessageWindowDays?: number
  /** Watermark visible screenshots with the workspace name. */
  watermarkScreenshots?: boolean
  /** When true, Workspace Settings rail tile is visible in READONLY mode and
   *  every panel renders read-only with all fields visible. */
  showAdminSettingsReadOnly?: boolean
}

export type WorkspaceSurfaceMaskPresetId =
  | 'MINIMAL'
  | 'SHOWCASE'
  | 'PORTFOLIO'
  | 'COMMUNITY'
  | 'EVERYTHING'
  | 'LEARN'

export const WORKSPACE_SURFACE_MASK_PRESETS: Record<
  WorkspaceSurfaceMaskPresetId,
  { label: string; description: string; mask: WorkspaceSurfaceMask }
> = {
  MINIMAL: {
    label: 'Minimal',
    description: 'Just Overview and Dashboard — the safe default for a newly-published workspace.',
    mask: { surfaces: { OVERVIEW: true, DASHBOARD: true }, sensitive: {} },
  },
  SHOWCASE: {
    label: 'Showcase',
    description: 'Overview, Dashboard, and Reports — good for a public portfolio-style workspace.',
    mask: {
      surfaces: { OVERVIEW: true, DASHBOARD: true, REPORTS: true, ANALYSIS: true },
      sensitive: { 'readonly.view_reports': true, 'readonly.view_analysis': true },
    },
  },
  PORTFOLIO: {
    label: 'Portfolio',
    description: 'Overview, Dashboard, Design, and Reports — great for creative work.',
    mask: {
      surfaces: { OVERVIEW: true, DASHBOARD: true, DESIGN: true, REPORTS: true, ANALYSIS: true },
      sensitive: { 'readonly.view_reports': true, 'readonly.view_analysis': true, 'readonly.view_uploads': true },
    },
  },
  COMMUNITY: {
    label: 'Community',
    description: 'Overview, Dashboard, Chat, and Forms — best for public groups.',
    mask: {
      surfaces: { OVERVIEW: true, DASHBOARD: true, CHAT: true, FORMS: true },
      sensitive: {},
      showMemberRoster: true,
    },
  },
  EVERYTHING: {
    label: 'Everything',
    description: 'All surfaces on. Use with intention — visitors see every screen.',
    mask: {
      surfaces: Object.fromEntries(WORKSPACE_SURFACE_KEYS.map((k) => [k, true])),
      sensitive: {},
    },
  },
  LEARN: {
    label: 'Learn (demo)',
    description: 'Full-tour preset for demo workspaces — every surface on, every sensitive field visible, Workspace Settings rendered read-only.',
    mask: {
      surfaces: Object.fromEntries(WORKSPACE_SURFACE_KEYS.map((k) => [k, true])),
      sensitive: {
        'readonly.view_usage': true,
        'readonly.view_billing': true,
        'readonly.view_member_contact_info': true,
        'readonly.view_uploads': true,
        'readonly.view_ai_history': true,
        'readonly.view_private_boards': true,
        'readonly.view_activity_logs': true,
        'readonly.view_reports': true,
        'readonly.view_analysis': true,
        'readonly.view_audit_logs': true,
        'readonly.view_invite_links': true,
        'readonly.view_settings': true,
      },
      showMemberRoster: true,
      showRealNames: true,
      allowExportPdf: true,
      chatMessageWindowDays: 365,
      showAdminSettingsReadOnly: true,
    },
  },
}

export const DEFAULT_WORKSPACE_SURFACE_MASK: WorkspaceSurfaceMask =
  WORKSPACE_SURFACE_MASK_PRESETS.MINIMAL.mask

/** Merge two masks; second wins for defined keys. */
export const mergeWorkspaceSurfaceMasks = (
  base: WorkspaceSurfaceMask | undefined,
  patch: WorkspaceSurfaceMask | undefined,
): WorkspaceSurfaceMask => {
  const merged: WorkspaceSurfaceMask = {
    surfaces: { ...(base?.surfaces ?? {}), ...(patch?.surfaces ?? {}) },
    sensitive: { ...(base?.sensitive ?? {}), ...(patch?.sensitive ?? {}) },
    showMemberRoster: patch?.showMemberRoster ?? base?.showMemberRoster,
    showRealNames: patch?.showRealNames ?? base?.showRealNames,
    allowExportPdf: patch?.allowExportPdf ?? base?.allowExportPdf,
    chatMessageWindowDays: patch?.chatMessageWindowDays ?? base?.chatMessageWindowDays,
    watermarkScreenshots: patch?.watermarkScreenshots ?? base?.watermarkScreenshots,
    showAdminSettingsReadOnly: patch?.showAdminSettingsReadOnly ?? base?.showAdminSettingsReadOnly,
  }
  return merged
}

export const isSurfaceAllowed = (
  mask: WorkspaceSurfaceMask | undefined,
  surface: WorkspaceSurfaceKey,
): boolean => {
  if (!mask) return surface === 'OVERVIEW' || surface === 'DASHBOARD'
  return mask.surfaces[surface] === true
}

export const normalizeWorkspaceSurfaceMask = (value: unknown): WorkspaceSurfaceMask | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const source = value as Partial<WorkspaceSurfaceMask> & Record<string, unknown>
  const surfaces: WorkspaceSurfaceMask['surfaces'] = {}
  const surfaceSource = source.surfaces && typeof source.surfaces === 'object'
    ? source.surfaces as Record<string, unknown>
    : {}
  for (const key of WORKSPACE_SURFACE_KEYS) {
    if (surfaceSource[key] === true) surfaces[key] = true
  }
  const sensitive: WorkspaceSurfaceMask['sensitive'] = {}
  const sensitiveSource = source.sensitive && typeof source.sensitive === 'object'
    ? source.sensitive as Record<string, unknown>
    : {}
  for (const [k, v] of Object.entries(sensitiveSource)) {
    if (v === true && k.startsWith('readonly.view_')) sensitive[k as keyof WorkspaceSurfaceMask['sensitive']] = true
  }
  const windowDaysRaw = source.chatMessageWindowDays
  const chatMessageWindowDays = typeof windowDaysRaw === 'number' && Number.isFinite(windowDaysRaw)
    ? Math.max(0, Math.round(windowDaysRaw))
    : undefined
  return {
    surfaces,
    sensitive,
    showMemberRoster: source.showMemberRoster === true,
    showRealNames: source.showRealNames === true,
    allowExportPdf: source.allowExportPdf === true,
    chatMessageWindowDays,
    watermarkScreenshots: source.watermarkScreenshots === true,
    showAdminSettingsReadOnly: source.showAdminSettingsReadOnly === true,
  }
}
